import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useEmailConfirmed } from '../../../application/store/authStore';
import { useHasCompletedOnboarding } from '../../../application/queries/onboardingQueries';
import { Loader, Center, Stack, Text } from '@mantine/core';
import { PUBLIC_ROUTES, CUSTOMER_ROUTES } from '../../routing/routes';

/**
 * CustomerRoute - Route guard for customer users only
 *
 * Requires authentication AND customer role AND email confirmed
 * SESSION_189: Added email confirmation enforcement
 * SESSION_199: Added onboarding redirect for uncompleted users
 * Sprint 041: Redirects to / (marketing homepage) if not authenticated
 *
 * Redirects to /confirm-email-pending if email not confirmed
 * Redirects to /access-denied if authenticated but not customer
 * Redirects to /onboarding if onboarding not completed (except when already on /onboarding)
 */
export const CustomerRoute: React.FC = (): React.ReactElement => {
  const { isAuthenticated, isCustomer, isLoading } = useAuth();
  const emailConfirmed = useEmailConfirmed();
  const location = useLocation();

  // SESSION_199: Fetch onboarding status (only when authenticated and email confirmed)
  // SESSION_201: Use useHasCompletedOnboarding which guarantees boolean (defaults to false on error)
  // SESSION_338: Extended to include pending business claim check for new business claim users
  const shouldCheckOnboarding = isAuthenticated && emailConfirmed && isCustomer;
  const { hasCompletedOnboarding, hasPendingBusinessClaim, isLoading: onboardingLoading } =
    useHasCompletedOnboarding(shouldCheckOnboarding);

  console.log('[CustomerRoute] Auth state:', { isAuthenticated, isCustomer, isLoading, emailConfirmed });
  console.log('[CustomerRoute] Onboarding state:', { shouldCheckOnboarding, onboardingLoading, hasCompletedOnboarding, hasPendingBusinessClaim });

  // Show loading state while checking authentication
  if (isLoading) {
    console.log('[CustomerRoute] ⏳ Loading auth, showing spinner...');
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Loading...
          </Text>
        </Stack>
      </Center>
    );
  }

  // Sprint 041: Redirect to home page (which has Login button) if not authenticated
  if (!isAuthenticated) {
    console.log('[CustomerRoute] ❌ Not authenticated, redirecting to home');
    return <Navigate to="/" replace />;
  }

  // SESSION_189: Redirect to email confirmation pending if email not confirmed
  if (!emailConfirmed) {
    console.log('[CustomerRoute] ❌ Email not confirmed, redirecting to confirmation page');
    return <Navigate to={PUBLIC_ROUTES.CONFIRM_EMAIL_PENDING} replace />;
  }

  // Redirect to access denied if not customer
  if (!isCustomer) {
    console.log('[CustomerRoute] ❌ Not a customer, redirecting to access-denied');
    return <Navigate to="/access-denied" replace />;
  }

  // SESSION_199: Show loading while checking onboarding status
  if (onboardingLoading) {
    console.log('[CustomerRoute] ⏳ Loading onboarding status...');
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text size="sm" c="dimmed">
            Loading...
          </Text>
        </Stack>
      </Center>
    );
  }

  // SESSION_199: Redirect to onboarding if not completed (except when already on /onboarding)
  // SESSION_201: hasCompletedOnboarding is guaranteed boolean (defaults to false on error)
  // SESSION_340: Users with pending business claims should STILL go through onboarding
  // to set their notification preferences. The onboarding page handles pending claims.
  if (!hasCompletedOnboarding) {
    const isOnOnboardingPage = location.pathname === CUSTOMER_ROUTES.ONBOARDING;
    if (!isOnOnboardingPage) {
      console.log('[CustomerRoute] ❌ Onboarding not completed, redirecting to onboarding');
      return <Navigate to={CUSTOMER_ROUTES.ONBOARDING} replace />;
    }
  }

  console.log('[CustomerRoute] ✅ Authenticated as customer with confirmed email and completed onboarding check, rendering routes');
  // Render child routes
  return <Outlet />;
};
